/**
 * RewardCelebration — a tangible "+₣ landed" burst (confetti + rising amount pop)
 * that fires when a rewarded-ad grant lands. It listens for the same
 * `fws:team-refresh` window event the ad-reward flow dispatches and reads
 * `detail.credited`. Anchored over the always-visible Net Worth pill so the
 * player sees the reward regardless of which tab they claimed it on.
 *
 * CSS-only motion (App.css: reward-pop-rise / confetti-burst) to match the
 * existing ScoreFloater / AnimatedNumber visual language.
 */
import { useEffect, useRef, useState } from 'react';
import { formatFranc } from '../lib/utils';

const CONFETTI_COLORS = ['#22c55e', '#fbbf24', '#3b82f6', '#ec4899', '#a855f7', '#ef4444'];
const PIECES = 14;

export default function RewardCelebration() {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(0);

  useEffect(() => {
    const onReward = (e) => {
      const credited = Number(e?.detail?.credited);
      if (!Number.isFinite(credited) || credited <= 0) return;
      const id = ++idRef.current;
      const pieces = Array.from({ length: PIECES }, (_, i) => ({
        key: i,
        x: ((Math.random() * 2 - 1) * 64).toFixed(0),
        y: (-(28 + Math.random() * 70)).toFixed(0),
        rot: (Math.random() * 360).toFixed(0),
        delay: Math.round(Math.random() * 90),
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      }));
      setBursts((b) => [...b, { id, amount: credited, pieces }]);
      setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1700);
    };
    window.addEventListener('fws:team-refresh', onReward);
    return () => window.removeEventListener('fws:team-refresh', onReward);
  }, []);

  if (!bursts.length) return null;

  return (
    <span className="reward-celebration" aria-hidden="true" data-testid="reward-celebration">
      {bursts.map((b) => (
        <span key={b.id} className="reward-burst">
          <span className="reward-pop" data-testid="reward-pop">💰&nbsp;{formatFranc(b.amount, { sign: true })}</span>
          <span className="reward-confetti">
            {b.pieces.map((p) => (
              <span
                key={p.key}
                className="reward-confetti-piece"
                style={{
                  '--cx': `${p.x}px`,
                  '--cy': `${p.y}px`,
                  '--cr': `${p.rot}deg`,
                  animationDelay: `${p.delay}ms`,
                  backgroundColor: p.color,
                }}
              />
            ))}
          </span>
        </span>
      ))}
    </span>
  );
}
